import { ContactStatusEnum, IContactRespSchema } from '@monetize/types/app';
import React, { FC, useCallback, useMemo, useRef } from 'react';
import { Link as ReactDomLink, useNavigate, useParams } from 'react-router-dom';
import {
  useActivateDeactivateContact,
  useUpdateContact,
} from '../../../api/contactsService';
import {
  MButton,
  MFlex,
  MHStack,
  MPageContentContainer,
  MPageHeader,
  MPageSearchInput,
} from '../../../components/Monetize';
import { MDataTableActions } from '../../../components/Monetize/DataTable';
import { useTableColumns } from '../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { ACTION_COLUMN_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import {
  getAccountContactCreate,
  getAccountContactEdit,
  getAccountContactView,
} from '../../../constants/routes';
import { useContacts } from '../../../hooks/AccountDetail';
import { useACL } from '../../../services/acl/acl';
import { ACLCheck } from '../../../services/acl/ACLCheck';
import { getIsTrulyEmptyList } from '../../../utils';
import {
  CONTACT_LIST_CATALOG,
  ContactListColumnContext,
} from './contactList.columns';

const ContactList: FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const accountId = params?.accountId || '';
  const { canDo } = useACL();
  const canUpdateAccountContacts = canDo([['account_contacts', 'update']]);
  const {
    filters,
    setFilters,
    onResetFilter,
    searchKey,
    searchTerm,
    setSearchTerm,
    listData,
    pager,
    setPager,
    loading,
    isError,
    error,
    refetch,
  } = useContacts(accountId);

  const getContactRoute = useCallback(
    (contactId: string) =>
      canUpdateAccountContacts
        ? getAccountContactEdit(accountId, contactId)
        : getAccountContactView(accountId, contactId),
    [accountId, canUpdateAccountContacts],
  );

  const onSelectRow = useCallback(
    (rowData: IContactRespSchema) => {
      navigate(getContactRoute(rowData?.id));
    },
    [getContactRoute, navigate],
  );

  const { mutate: activateDeactivateMutate } = useActivateDeactivateContact();
  const { mutate: doUpdateContact } = useUpdateContact();

  const actionsBodyTemplate = useCallback(
    (rowData: IContactRespSchema) => {
      const { status, primary } = rowData;
      const actions = [
        {
          title: 'Set as Primary',
          enabled:
            !primary &&
            status === ContactStatusEnum.ACTIVE &&
            canUpdateAccountContacts,
          action: () =>
            doUpdateContact({
              contactId: rowData.id,
              payload: {
                ...rowData,
                primary: true,
              },
            }),
        },
        {
          title: 'Edit',
          enabled: canUpdateAccountContacts,
          action: () => onSelectRow(rowData),
        },
        {
          title: 'Activate',
          enabled:
            status === ContactStatusEnum.CANCELED && canUpdateAccountContacts,
          action: () =>
            activateDeactivateMutate({
              action: 'activate',
              accountId,
              contactId: rowData.id,
            }),
        },
        {
          title: 'Deactivate',
          color: 'danger',
          enabled:
            !primary &&
            status === ContactStatusEnum.ACTIVE &&
            canUpdateAccountContacts,
          action: () =>
            activateDeactivateMutate({
              action: 'cancel',
              accountId,
              contactId: rowData.id,
            }),
        },
      ];
      return <MDataTableActions actions={actions} />;
    },
    [
      accountId,
      activateDeactivateMutate,
      canUpdateAccountContacts,
      doUpdateContact,
      onSelectRow,
    ],
  );

  const tableRef = useRef<DataTableRef>(null);

  const columnContext = useMemo<ContactListColumnContext>(
    () => ({ actionBodyTemplate: actionsBodyTemplate, getContactRoute }),
    [actionsBodyTemplate, getContactRoute],
  );
  const { columns, state: columnState } = useTableColumns(
    CONTACT_LIST_CATALOG,
    columnContext,
  );

  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading,
    totalElements: listData?.totalElements || 0,
    filters,
    searchTerm,
    page: pager.page,
  });

  const filterComponentReset = React.useRef<any>(null);
  const searchComponentReset = React.useRef<any>(null);

  const resetInternalFilters = () => {
    filterComponentReset.current && filterComponentReset.current();
    searchComponentReset.current && searchComponentReset.current();
  };

  return (
    <MPageContentContainer className="datatable-responsive">
      <MPageHeader title="Contacts" hideContent={isTrulyEmptyList}>
        <MFlex>
          <MHStack gap="2">
            <MPageSearchInput
              value={searchTerm || ''}
              onChange={(e: any) => setSearchTerm && setSearchTerm(e)}
              count={listData?.totalElements}
              resetSearch={searchComponentReset}
              placeholderKey="Name"
              mode="alwaysExpanded"
            />
            <RefreshTableButton refetch={refetch} />
            <ExportEntityButton
              entity="contacts"
              filters={filters}
              searchKey={searchKey}
              searchTerm={searchTerm}
              sortField={pager.sortField}
              sortOrder={pager.sortOrder}
              endpointParams={[accountId]}
              getFilename={() => `contacts-${accountId}.csv`}
            />
            <ACLCheck acls={[['account_contacts', 'create']]}>
              <MButton variant="secondary" asChild>
                <ReactDomLink to={getAccountContactCreate(accountId)}>
                  New Contact
                </ReactDomLink>
              </MButton>
            </ACLCheck>
          </MHStack>
        </MFlex>
      </MPageHeader>

      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={listData?.content || []}
        isLoading={loading}
        enablePagination
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={listData?.totalElements || 0}
        totalPages={listData?.totalPages}
        pager={pager}
        setPager={setPager}
        onRowClick={(row) => onSelectRow(row)}
        onFiltersChange={(newFilters) => {
          setFilters(newFilters);
        }}
        resetFilter={() => {
          resetInternalFilters();
          onResetFilter();
        }}
        emptyProps={{
          mainMessage: 'Looks like there are no contacts here.',
          btnLabel: canDo([['account_contacts', 'create']])
            ? 'New Contact'
            : '',
          to: getAccountContactCreate(accountId),
        }}
      />
    </MPageContentContainer>
  );
};

export default ContactList;
